import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Divider } from "@mui/material";
import { Card, Chip, DataRow } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { Edit04Icon } from "@hugeicons-pro/core-stroke-rounded";
import PlatformIntegrationFieldType from "enums/PlatformIntegrationFieldType";
import {
  integrationValue,
  PlatformIntegration,
  PlatformIntegrationField,
  PlatformIntegrationTemplateSection,
} from "hooks/usePlatformIntegrations";
import {
  CopyableDetailRow,
  ProseDetailRow,
} from "components/common/detailRows";
import { Stack } from "components/layout/reusableLayouts";

interface IntegrationSectionCardProps {
  integration: PlatformIntegration;
  section: PlatformIntegrationTemplateSection;
  onEdit: () => void;
}

/** One section of a provider's template, read back. A secret or a concealed field shows only whether it is set. */
const IntegrationSectionCard: React.FC<IntegrationSectionCardProps> = ({
  integration,
  section,
  onEdit,
}) => {
  const { t } = useTranslation();

  const presence = (field: PlatformIntegrationField, set: boolean) =>
    set ? (
      <Chip size="sm" tone="positive" dot label={t("Stored")} />
    ) : (
      <Chip
        size="sm"
        tone={
          integration.missingFields.includes(field.key) ? "warning" : "neutral"
        }
        dot
        label={t("Not set")}
      />
    );

  const fieldRow = (field: PlatformIntegrationField) => {
    const label = t(field.labelKey);
    if (field.type === PlatformIntegrationFieldType.SECRET) {
      return (
        <DataRow
          key={field.key}
          label={label}
          primaryTrailing={presence(
            field,
            integration.storedSecrets.includes(field.key),
          )}
        />
      );
    }

    const stored = integrationValue(integration, field.key);
    const value =
      field.type === PlatformIntegrationFieldType.SELECT
        ? field.options?.find(
            (option) => option.value === (stored || field.defaultValue),
          )?.label
        : stored;
    if (field.concealed || !value) {
      return (
        <DataRow
          key={field.key}
          label={label}
          primaryTrailing={presence(field, !!value)}
        />
      );
    }
    if (field.type === PlatformIntegrationFieldType.MULTILINE) {
      return <ProseDetailRow key={field.key} label={label} value={value} />;
    }
    return <DataRow key={field.key} label={label} primaryTrailing={value} />;
  };

  const rows = section.fields.flatMap((field) => [
    ...(field.endpointUrl
      ? [
          <CopyableDetailRow
            key={`${field.key}-endpoint`}
            label={t("Endpoint URL")}
            value={field.endpointUrl}
            copyLabel={t("Copy {{field}}", { field: field.endpointUrl })}
            copiedMessage={t("WebhookUrlCopiedToast")}
            canCopy
          />,
        ]
      : []),
    fieldRow(field),
  ]);

  return (
    <Card
      variant="outlined"
      title={t(section.labelKey)}
      subtitle={section.descriptionKey ? t(section.descriptionKey) : undefined}
      action={{
        type: "content",
        content: (
          <Button
            variant="default-outlined"
            size="small"
            startIcon={<HugeiconsIcon icon={Edit04Icon} size={16} />}
            onClick={onEdit}
          >
            {t("Edit")}
          </Button>
        ),
      }}
    >
      <Stack gap="var(--space-0)">
        {rows.map((row, index) => (
          <React.Fragment key={row.key}>
            {index > 0 && <Divider />}
            {row}
          </React.Fragment>
        ))}
      </Stack>
    </Card>
  );
};

export default IntegrationSectionCard;
